import { useState } from "react";

import { useGoogleLogin, CodeResponse } from "@react-oauth/google";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

import commonservice from "@apis/commonservice";
import { useModalTip } from "@components/ModalAutoAlert";
import { LOCALSTORAGE_KEY } from "@config";
import { useShowLoginLimitModal } from "@pages/home/login/LoginLimitModal/hooks";
import report from "@report";

/**
 * 使用 Google Identity Services 官方弹窗模式实现 Google 登录授权
 *
 * 注意：此 hook 使用 Google 官方的 popup 模式获取 authorization code。
 * 后端在验证 code 时需要使用 "postmessage" 作为 redirect_uri，
 * 而不是原来的 "/google/login" 回调 URL。
 *
 * @param type 授权类型: login | signup | authorize
 * @param successFn 登录成功回调
 * @param failFn 登录失败回调
 * @param authCb 授权成功回调（用于 authorize 类型）
 */
export const usePopoverGoogleAuth = (
  type: "login" | "signup" | "authorize" | "" = "",
  successFn?: (data: any, fn?: any) => void,
  failFn?: (err?: any) => void,
  authCb?: () => void,
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { setShowLoginLimitModal, setLoginLimitNumber } = useShowLoginLimitModal();

  const errorModalTip = useModalTip({ type: "error", title: "You have canceled." });
  const userModalTip = useModalTip({ type: "info", title: "Verified successfully" });
  const successModalTip = useModalTip({
    type: "confirm",
    title: "Verified successfully",
    subtitle: "You can also log in with this Google account.",
  });

  // 处理登录/注册请求
  const handleLoginOrSignup = async (code: string) => {
    setLoading(true);
    report.event("google_callback", {
      kalo_pos: "google_callback_count",
      kalo_type: type,
    });

    try {
      const data: any = await commonservice.googleLogin({ code, type });

      // 登录成功后保存 token
      if (data.accessToken) {
        localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, data.accessToken);
      }

      if (successFn) {
        successFn(data, (userInfo: any) => {
          // 设置登录类型是 google
          localStorage.setItem(LOCALSTORAGE_KEY.LOGIN_TYPE, "google");
          sessionStorage.setItem("showGoogleInfo", "true");
          setLoading(false);
          report.event("link_success", {
            kalo_type: "google_" + type,
            kalo_username: userInfo.username || userInfo.email,
          });
        });
      }
    } catch (err: any) {
      report.event("link_error", {
        kalo_type: "google_" + type,
        kalo_json: { err },
      });
      setLoading(false);

      if (err.code === "1303") {
        setLoginLimitNumber(err.message);
        setShowLoginLimitModal(true);
      } else {
        if (err.code === "1110") {
          err.message = t("The Google account is linked to another account. You can try another Google account.");
        }
        if (err.code === "1106") {
          err.message = t('Oops, something seems to be wrong, please "re-authorize".');
        }
        errorModalTip.open({
          title: "Verified unsuccessfully",
          subtitle: err.message,
        });
      }
      failFn && failFn(err);
    }
  };

  // 处理授权请求
  const handleAuthorize = async (code: string) => {
    setLoading(true);
    report.event("google_callback", {
      kalo_pos: "google_callback_count",
      kalo_type: type,
    });

    try {
      await commonservice.verifyAccount({
        loginMethod: "GOOGLE",
        code,
      });

      report.event("link_success", {
        kalo_type: "google_" + type,
      });

      successModalTip.open({
        type: "confirm",
        title: "Verified successfully",
        subtitle: "You can also log in with this Google account.",
      });

      authCb && authCb();
    } catch (err: any) {
      report.event("link_error", {
        kalo_type: "google_" + type,
        kalo_json: { err },
      });

      if (err.code === "1303") {
        setLoginLimitNumber(err.message);
        setShowLoginLimitModal(true);
      } else {
        if (err.code === "1110") {
          err.message = t("The Google account is linked to another account. You can try another Google account.");
        }
        if (err.code === "1106") {
          err.message = t('Oops, something seems to be wrong, please "re-authorize".');
        }
        errorModalTip.open({
          title: "Verified unsuccessfully",
          subtitle: err.message,
        });
      }

      failFn && failFn(err);
    } finally {
      setLoading(false);
    }
  };

  // 使用 Google Identity Services 官方 popup 模式
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    onSuccess: (codeResponse: CodeResponse) => {
      report.click({
        kalo_pos: "google_popup_success",
        kalo_type: type,
      });

      const code = codeResponse.code;
      if (code) {
        // 根据类型处理
        if (type === "login" || type === "signup") {
          handleLoginOrSignup(code);
        } else if (type === "authorize") {
          handleAuthorize(code);
        }
      }
    },
    onError: (errorResponse) => {
      setLoading(false);
      report.event("link_error", {
        kalo_type: "google_" + type,
        kalo_value: errorResponse.error || "Authorization failed",
      });
      errorModalTip.open({
        type: "error",
        title: "Authorization Failed",
        subtitle: errorResponse.error_description || errorResponse.error || "Unknown error",
      });
      failFn && failFn(errorResponse);
    },
    onNonOAuthError: (nonOAuthError) => {
      setLoading(false);
      // 处理非 OAuth 错误（如弹窗被关闭）
      if (nonOAuthError.type === "popup_closed") {
        report.event("link_error", {
          kalo_type: "google_" + type,
          kalo_value: "User closed popup",
        });
        // 用户主动关闭弹窗，不显示错误提示
      } else if (nonOAuthError.type === "popup_failed_to_open") {
        errorModalTip.open({
          title: "Popup Blocked",
          subtitle: t("Please allow popups for this website to use Google login."),
        });
      }
    },
    scope: "openid email profile",
    select_account: true,
  });

  // 触发 Google 登录弹窗
  const googlePopoverLink = (linkType: "login" | "signup" | "me") => {
    if (!type) return;

    console.log("[usePopoverGoogleAuth] 触发 Google 弹窗登录", { type, linkType });

    setLoading(true);
    report.click({
      kalo_pos: "click_google_popover_link",
      kalo_type: linkType,
    });

    // 调用 Google Identity Services 官方弹窗登录
    console.log("[usePopoverGoogleAuth] 调用 googleLogin()");
    googleLogin();
  };

  // 授权回来 loading 等待
  const SpinDom = () => {
    return (
      <div className="spin">
        {loading && (
          <div className="google_loading">
            <Spin tip={t("Loading") + "..."}></Spin>
          </div>
        )}
        {errorModalTip.showDom()}
        {userModalTip.showDom()}
        {successModalTip.showDom()}
      </div>
    );
  };

  return {
    googlePopoverLink,
    SpinDom,
    loading,
  };
};
